import { ApiResult, CrudService, Meta } from 'ui-shared';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    PdmContadorEntity,
    ProdutoSituacaoSaneamentoEntity,
    SaneamentoAtributosCommand,
    SaneamentoAuditoriaAvaliacaoEntity,
    SaneamentoAuditoriaEntity,
    SaneamentoPriorizacaoGridResult,
    SaneamentoProdutoEntity,
    SaneamentoUnidadeNegocioEntity,
    SolicitacaoProcessamentoEntity
} from '@catalogo/models';
import { SaneamentoDescricaoEntity, SaneamentoEntity } from '@catalogo/models/saneamento.entity';
import { SaneamentoFabricanteEntity } from '@catalogo/models/saneamento-fabricante.entity';
import { SaneamentoEmpresaEntity } from '@catalogo/models/saneamento-empresa.entity';
import { SaneamentoDetalhamentoEntity } from '@catalogo/models/saneamento-detalhamento.entity';
import { SaneamentoDetalhamentoAtributoEntity } from '@catalogo/models/saneamento-detalhamento-atributo.entity';
import { SaneamentoDetalhamentoAtributosCommand } from '@catalogo/models/saneamento-detalhamento-atributos.command';
import { OPERACAO_API_URL } from "@catalogo/features/compartilhado/const/constantes";
import { Observable } from "rxjs";

@Injectable({ providedIn: 'any' })
export class SaneamentoDataService extends CrudService<SaneamentoProdutoEntity> {

    protected url: string;

    constructor(
        @Inject(OPERACAO_API_URL) urlBase: string,
        @Inject(HttpClient) protected httpClient: HttpClient
    ) {
        super(urlBase, httpClient);
        this.url = `${urlBase}/v1/Saneamento`;
    }

    public associar = (record: SaneamentoPriorizacaoGridResult) =>
        this.httpClient.post<SaneamentoPriorizacaoGridResult>(this.url + '/AssociarProduto', {
            produtoId: record.id,
            produtoVersaoId: record.produtoVersaoId,
            pdmVersaoId: record.pdmVersaoId
        });

    public obterTodosVersaoPdm = (meta: Meta) =>
        this.getWithQueryBase<PdmContadorEntity>(this.url + '/versaopdm', meta);

    public obterTodosAuditoria = (meta: Meta) =>
        this.getWithQueryBase<SaneamentoAuditoriaEntity>(this.url + '/EmAuditoria', meta);

    public obterEmAuditoria = (saneamentoId: string) =>
        this.httpClient.get<ApiResult<SaneamentoAuditoriaEntity>>(`${this.url}/EmAuditoria/${saneamentoId}`);

    public obterTodosPriorizacao = (meta: Meta) =>
        this.getWithQueryBase<SaneamentoPriorizacaoGridResult>(this.url + '/priorizacao', meta);

    public obterDetalhado = (saneamentoId: string, unidadeNegocioId?: string, obterAvaliacao?: boolean, removerNaoAtendidos?: boolean) =>
        this.httpClient.get<ApiResult<SaneamentoEntity>>(`${this.url}/${saneamentoId}/Detalhado?unidadeNegocioId=${unidadeNegocioId}&obterAvaliacao=${obterAvaliacao}&removerNaoAtendidos=${removerNaoAtendidos}`);

    public obterDescricoes = (saneamentoId: string) =>
        this.httpClient.get<ApiResult<SaneamentoDescricaoEntity[]>>(`${this.url}/${saneamentoId}/ObterDescricao`);

    public gerarDescricoes = (saneamentoId: string) =>
        this.httpClient.get<ApiResult<SaneamentoDescricaoEntity[]>>(`${this.url}/${saneamentoId}/GerarDescricao`);

    public aprovar = (saneamentoId: string, unidadeNegocioId: string) =>
        this.httpClient.post(`${this.url}/${saneamentoId}/Aprovar`, { unidadeNegocioId });

    public reprovar = (record: SaneamentoAuditoriaAvaliacaoEntity) =>
        this.httpClient.post<SaneamentoAuditoriaAvaliacaoEntity>(`${this.url}/${record.saneamentoId}/Reprovar`, record);

    public obterUnidadesNegocio = (saneamentoId: string, meta: Meta) =>
        this.getWithQueryBase<SaneamentoUnidadeNegocioEntity>(`${this.url}/${saneamentoId}/unidadenegocio`, meta);

    public obterFabricantes = (saneamentoId: string, meta: Meta) =>
        this.getWithQueryBase<SaneamentoFabricanteEntity>(`${this.url}/${saneamentoId}/Fabricantes`, meta);

    public obterEmpresas = (saneamentoId: string) =>
        this.httpClient.get<ApiResult<SaneamentoEmpresaEntity[]>>(`${this.url}/${saneamentoId}/Empresas`);

    public obterTodosDetalhamento = (meta: Meta) =>
        this.getWithQueryBase<SaneamentoEntity>(this.url + '/EmDetalhamento', meta);

    public obterTodosDetalhamentos = (id: string, meta: Meta) =>
        this.getWithQueryBase<SaneamentoDetalhamentoEntity>(`${this.url}/${id}/EmDetalhamento`, meta);

    public obterDetalhamentoAtualPorSaneamentoId = (id: string) =>
        this.httpClient.get<ApiResult<SaneamentoDetalhamentoEntity>>(`${this.url}/${id}/EmDetalhamento/Atual`);

    public obterDetalhamentoPorId = (saneamentoId: string, detalhamentoId: string) =>
        this.httpClient.get<ApiResult<SaneamentoDetalhamentoEntity>>(`${this.url}/${saneamentoId}/EmDetalhamento/${detalhamentoId}`)

    public obterDetalhamentoAtributos = (saneamentoDetalhamentoId: string) =>
        this.httpClient.get<ApiResult<SaneamentoDetalhamentoAtributoEntity[]>>(`${this.url}/EmDetalhamento/${saneamentoDetalhamentoId}/Atributos`)

    public solicitarDetalhamento = (command: SaneamentoAtributosCommand) =>
        this.httpClient.post<ApiResult<boolean>>(`${this.url}/${command.saneamentoId}/EmDetalhamento`, command);

    public salvarRespostas = (saneamentoDetalhamentoAtributos: SaneamentoDetalhamentoAtributosCommand) =>
        this.httpClient.post<ApiResult<boolean>>(`${this.url}/EmDetalhamento`, saneamentoDetalhamentoAtributos);

    public retornarDetalhamento = (saneamentoDetalhamentoAtributos: SaneamentoDetalhamentoAtributosCommand) =>
        this.httpClient.post<ApiResult<boolean>>(`${this.url}/EmDetalhamento/Retorno`, saneamentoDetalhamentoAtributos);

    public copiar = (produtoVersaoId: string) =>
        this.httpClient.post<ApiResult<boolean>>(`${this.url}/Copiar`, {produtoVersaoId});

    public sugerirPreenchimentoSaneamentos = (saneamentoIds: string[]) =>
        this.httpClient.post<ApiResult<SolicitacaoProcessamentoEntity>>(`${this.url}/SugestaoPreenchimento`, saneamentoIds);

    public importar = (payload: FormData) => this.httpClient.post<any>(`${this.url}/SolicitarImportacao`, payload);

    public exportar = (pdmVersaoId: string, meta: Meta) =>
        this.httpClient.post<ApiResult<string>>(`${this.url}/SolicitarExportacao`, { pdmVersaoId, ...meta });

    public obterUltimaSolicitacaoImportacao = (pdmVersaoId: string) => this.httpClient.get<ApiResult<SolicitacaoProcessamentoEntity>>(`${this.url}/Importar/Atual/${pdmVersaoId}`);

    public downloadRetornoImportacaoPlanilha = (pdmVersaoId: string): Observable<HttpResponse<Blob>> => {
        return this.httpClient.get(`${this.url}/Importar/Retorno/${pdmVersaoId}`, {
            observe: 'response',
            responseType: 'blob'
        });
    }

    public obterUltimaSolicitacaoExportacao = (pdmVersaoId: string) => this.httpClient.get<ApiResult<SolicitacaoProcessamentoEntity>>(`${this.url}/Exportar/Atual/${pdmVersaoId}`);

    public downloadRetornoExportacaoPlanilha = (pdmVersaoId: string): Observable<HttpResponse<Blob>> => {
        return this.httpClient.get(`${this.url}/Exportar/Retorno/${pdmVersaoId}`, {
            observe: 'response',
            responseType: 'blob'
        });
    }

    public getSituacaoSaneamento = () => this.httpClient.get<ApiResult<ProdutoSituacaoSaneamentoEntity>>(`${this.url}/Situacao/Home`);

    public sugerirPreenchimentoSaneamentoPorVersaoPdm(listaPdmVersoesIds: string[]) {
        return this.httpClient.post<ApiResult<any>>(`${this.url}/SugerirPreenchimentoPorPdmVersao`, listaPdmVersoesIds);
    }

    public exportarAuditoria = (meta: Meta) =>
        this.httpClient.post<ApiResult<string>>(`${this.url}/Auditoria/SolicitarExportacao`, { ...meta });

    public importarAuditoria = (payload: FormData) =>
        this.httpClient.post<any>(`${this.url}/Auditoria/SolicitarImportacao`, payload);

    public obterUltimaSolicitacaoExportacaoAuditoria = () =>
        this.httpClient.get<ApiResult<SolicitacaoProcessamentoEntity>>(`${this.url}/Auditoria/Exportar/Atual`);

    public obterUltimaSolicitacaoImportacaoAuditoria = () =>
        this.httpClient.get<ApiResult<SolicitacaoProcessamentoEntity>>(`${this.url}/Auditoria/Importar/Atual`);

    public downloadRetornoExportacaoPlanilhaAuditoria = (): Observable<HttpResponse<Blob>> => {
        return this.httpClient.get(`${this.url}/Auditoria/Exportar/Retorno`, {
            observe: 'response',
            responseType: 'blob'
        });
    }

    public downloadRetornoImportacaoPlanilhaAuditoria = (): Observable<HttpResponse<Blob>> => {
        return this.httpClient.get(`${this.url}/Auditoria/Importar/Retorno`, {
            observe: 'response',
            responseType: 'blob'
        });
    }

    public atualizarDataReferencia = (saneamentoId: string, dataReferencia: Date) =>
        this.httpClient.put<ApiResult<boolean>>(`${this.url}/${saneamentoId}/DataReferencia`, { dataReferencia });
}
